import { useMediaQuery, useTheme, type Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import { PromoBannerSlot, richHtmlSx } from '@onesource/components';
import type { TSpaceBase } from '@onesource/schemas';
import { PromoBannerPosition } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { sanitizeRichHtml } from '@onesource/utils';
import DOMPurify from 'dompurify';
import { useEffect, useState } from 'react';
import { CiImageOn } from 'react-icons/ci';

import { hasHTML } from '#/utils/has-html';

import { SpaceModuleCard } from './space-module-card';

interface SpaceHomePageProps {
  space: TSpaceBase;
  modules: TSpaceBase[];
  rightColumnContent?: React.ReactNode;
}

/**
 * Space Home Page
 * Banner image at top, then two-column layout: description (left) + modules (right)
 * Based on Figma design
 */
export const SpaceHomePage = ({ space, modules, rightColumnContent }: SpaceHomePageProps) => {
  // Sanitize HTML description
  const sanitizedDescription = space.description ? sanitizeRichHtml(space.description) : '';
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [bannerFailedToLoad, setBannerFailedToLoad] = useState(false);
  useEffect(() => {
    setBannerFailedToLoad(false);
  }, [space.bannerImage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: { xs: '100%', md: 1060 } }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        sx={{
          mb: 2,
          fontFamily: '"Figtree", sans-serif',
          fontSize: { xs: 14, md: 16 },
          lineHeight: { xs: '20px', md: '22px' },
          fontWeight: 400,
          color: theme => theme.palette.grey[600],
          '& .MuiBreadcrumbs-separator': {
            mx: 0.6,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{ mt: hasHTML(space.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 }, color: theme.palette.grey[600] }}
        >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(space.name) }} />
        </Typography>
        <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
          Home{' '}
        </Typography>
      </Breadcrumbs>

      {/* Banner Image */}
      {space.bannerImage && getMediaUrl(space.bannerImage) && !bannerFailedToLoad ? (
        <Box
          component="img"
          src={getMediaUrl(space.bannerImage)}
          alt={space.name}
          onError={() => setBannerFailedToLoad(true)}
          sx={{
            width: '100%',
            height: { xs: 93, sm: 200, md: 279 },
            borderRadius: { xs: '5px', md: '15px' },
            objectFit: 'cover',
            bgcolor: '#ede0dd',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: { xs: 93, md: 279 },
            borderRadius: { xs: '5px', md: '15px' },
            overflow: 'hidden',
            bgcolor: '#ede0dd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CiImageOn size={64} color="#d8aaa2" />
        </Box>
      )}

      {/* Two Column Layout */}
      <Box
        sx={{
          mt: { xs: 1.5, md: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '30px', md: '32px', lg: '40px' },
          alignItems: { xs: 'inherit', md: 'flex-start' },
          width: '100%',
        }}
      >
        {/* Left Column - Title and Description (57.6% of content area) */}
        <Box
          sx={{
            flex: '1 1 60%',
            maxWidth: { xs: '100%', md: 620 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '2px', md: '10px' },
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: { xs: 2, md: 2.5 },
          }}
        >
          {/* Description - rendered as HTML */}
          {sanitizedDescription && (
            <>
              <Box
                sx={{
                  ...richHtmlSx,
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: 12, md: 14 },
                  lineHeight: '19px',
                  color: 'black',
                  '& strong, & b': {
                    fontWeight: 700,
                  },
                  ...(isMobile &&
                    !isDescriptionExpanded && {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }),
                }}
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
              {isMobile && (
                <Typography
                  component="span"
                  onClick={() => setIsDescriptionExpanded(prev => !prev)}
                  sx={{
                    fontFamily: '"Figtree", sans-serif',
                    fontSize: { xs: 12, md: 12 },
                    fontWeight: 700,
                    color: theme.palette.grey[600],
                    cursor: 'pointer',
                  }}
                >
                  {isDescriptionExpanded ? 'Read Less...' : 'Read More...'}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Right Column - Modules or custom content (42.4% of content area) */}
        <Box
          sx={{
            flex: '1 1 43%',
            maxWidth: { xs: '100%', md: 400 },
            display: 'flex',
            flexDirection: 'column',
            gap: '17px',
          }}
        >
          {rightColumnContent ??
            modules.map((module, index) => (
              <SpaceModuleCard
                key={module.id}
                module={module}
                parentSpaceId={space.id}
                index={index + 1}
                isLocked={module.isLocked}
                prerequisiteName={module.prerequisiteName}
                showIndexBadge={space.showIndexBadge !== false}
              />
            ))}

          {/* Promo banner — middle */}
          <PromoBannerSlot entityId={space.id} position={PromoBannerPosition.MIDDLE} />
        </Box>
      </Box>

      {/* Promo banner — footer */}
      <PromoBannerSlot
        entityId={space.id}
        position={PromoBannerPosition.FOOTER}
        sx={{
          width: { xs: '100%', md: 995 },
          position: { xs: 'sticky', md: 'static' },
          bottom: { xs: 16, md: 'auto' },
          zIndex: { xs: theme.zIndex.appBar, md: 'auto' },
        }}
      />
    </Box>
  );
};
